/**
 * A visão geral do CLIENTE sobre as detecções de uma janela — pura, sem React.
 *
 * O servidor só conta severidades (`/stats/detections`); o resto — o que foi
 * contido sozinho, o que pede atenção, ameaças por dia, o que tentaram fazer —
 * sai da lista de alertas da janela, carregada até um teto. Passou do teto, os
 * números valem para os N mais recentes, e a tela diz isso.
 */
import type { DetectionAlert, DetectionSeverity } from '../sse/types.ts'
import { summarize, tacticPlainLabel } from './semantics.ts'
import type { Ranked } from './semantics.ts'

export interface ThreatBucket {
  /** Início do balde, epoch ms (UTC). */
  start: number
  critical: number
  high: number
  medium: number
  low: number
  other: number
}

export interface ThreatOverview {
  /** Alertas agregados. */
  loaded: number
  /** A janela tinha mais alertas que o teto: os números são dos mais recentes. */
  truncated: boolean
  /** Alertas com ao menos uma ação EXECUTADA pelo agente (simulada não conta). */
  contained: number
  /** Alertas com alguma ação que FALHOU: máquina segue comprometida. */
  failed: number
  /** Críticos sem nenhuma ação executada. */
  criticalOpen: number
  buckets: ThreatBucket[]
  /** Só as táticas vistas, da mais frequente para a menos. */
  tactics: Array<{ id: string; label: string; count: number }>
  hosts: Ranked[]
  /** Os mais recentes primeiro (ordem do servidor). */
  recent: DetectionAlert[]
}

export const isContained = (alert: DetectionAlert): boolean =>
  alert.response.some((r) => r.status === 'executed')

export const hasFailed = (alert: DetectionAlert): boolean =>
  alert.response.some((r) => r.status === 'failed')

const HOUR = 3_600_000
const DAY = 24 * HOUR

/** Balde por hora até 2 dias; por dia acima disso. Sempre alinhado em UTC. */
export function bucketSize(spanMs: number): number {
  return spanMs <= 2 * DAY ? HOUR : DAY
}

function emptyBuckets(from: number, to: number, size: number): ThreatBucket[] {
  const out: ThreatBucket[] = []
  for (let start = Math.floor(from / size) * size; start <= to; start += size) {
    out.push({ start, critical: 0, high: 0, medium: 0, low: 0, other: 0 })
  }
  return out
}

export function buildThreatOverview(
  alerts: readonly DetectionAlert[],
  range: { from: number; to: number },
  truncated: boolean,
): ThreatOverview {
  const size = bucketSize(range.to - range.from)
  const buckets = emptyBuckets(range.from, range.to, size)
  const first = buckets[0]?.start ?? range.from

  let contained = 0
  let failed = 0
  let criticalOpen = 0
  for (const alert of alerts) {
    const ok = isContained(alert)
    if (ok) contained += 1
    if (hasFailed(alert)) failed += 1
    if (alert.severity === 'critical' && !ok) criticalOpen += 1

    const at = Date.parse(alert.row.received_at)
    if (!Number.isFinite(at)) continue
    const bucket = buckets[Math.floor((at - first) / size)]
    if (bucket === undefined) continue
    const key: DetectionSeverity | 'other' = alert.severity ?? 'other'
    bucket[key] += 1
  }

  const summary = summarize(alerts)
  const tactics = summary.tactics
    .filter((t) => t.count > 0)
    .map((t) => ({ id: t.id, label: tacticPlainLabel(t.id, t.label), count: t.count }))
    .sort((a, b) => b.count - a.count)

  return {
    loaded: alerts.length,
    truncated,
    contained,
    failed,
    criticalOpen,
    buckets,
    tactics,
    hosts: summary.hosts,
    recent: alerts.slice(0, 6),
  }
}
